import { ReactNode } from "react";

import { cn } from "@reactive-resume/utils";
import { RichInput, Switch, Tooltip } from "@reactive-resume/ui";
import { defaultSections } from "@reactive-resume/schema";

import { useResumeStore } from "@/client/stores/resume";
import { AiActions } from "@/client/components/ai-actions";

import AiAssistantIcon from "../../../Icons/AiAssistantIcon";

import { CollapsibleSection } from "../../CollapsibleSection";

import { SectionIcon } from "./shared/section-icon";
import { SectionOptions } from "./shared/section-options";
import { EyeSlash } from "@phosphor-icons/react";

export const SummarySection = () => {
  const setValue = useResumeStore((state) => state.setValue);
  const section = useResumeStore(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    (state) => state.resume.data.sections.summary ?? defaultSections.summary,
  );

  const headerHTML: ReactNode = (
    <div className="flex w-full justify-between">
      <div className="flex items-center gap-x-4">
        <SectionIcon id="summary" size={18} />
        <h2 className="line-clamp-1 text-base font-bold text-gray-800 lg:text-xl">Summary</h2>
      </div>

      <div className="flex items-center gap-1">
        {/* Show EyeSlash icon if section not visible */}
        {!section.visible && (
          <Tooltip content="This section is not visible">
            <div onClick={(e) => e.stopPropagation()} className="text-red-500 opacity-70">
              <EyeSlash size={18} />
            </div>
          </Tooltip>
        )}
        <div onClick={(e) => e.stopPropagation()}>
          <SectionOptions id="summary" />
        </div>
      </div>
    </div>
  );

  const contentHTML: ReactNode = (
    <main className={cn(!section.visible && "opacity-50")}>
      <RichInput
        content={section.content}
        footer={(editor) => (
          <div>
            <div className="flex w-full items-center justify-end gap-x-2 pt-2">
              <span className="text-muted-foreground text-xs">
                {section.canAICustomize ? (
                  <Tooltip content="Customization for each job application using AI is Enabled">
                    <span className="flex items-center gap-x-1">
                      <AiAssistantIcon size={18} />
                      Tailor for Each Job
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip content="Customization for each job application using AI is Disabled, turn on to enable">
                    <span className="flex items-center gap-x-1 opacity-50 grayscale">
                      <AiAssistantIcon size={18} className="" />
                      Tailor for Each Job
                    </span>
                  </Tooltip>
                )}
              </span>
              <Switch
                className="data-[state=checked]:bg-[#047857]"
                aria-label={
                  section.canAICustomize
                    ? "Toggle customization to disabled"
                    : "Toggle customization to enabled"
                }
                checked={section.canAICustomize}
                onCheckedChange={(checked) => setValue("sections.summary.canAICustomize", checked)}
              />
            </div>

            <AiActions
              value={editor.getText()}
              onChange={(value) => {
                editor.commands.setContent(value, true);
                setValue("sections.summary.content", value);
              }}
            />
          </div>
        )}
        onChange={(value) => {
          setValue("sections.summary.content", value);
        }}
      />
    </main>
  );

  return <CollapsibleSection id="summary" header={headerHTML} content={contentHTML} />;
};
